import * as React from 'react';
import { useContext, useState } from 'react';
import { ApiVersion, VegobjekttypeCategoryState, VegobjekttypeState } from '@/state';
import { ConfigAndDataContext } from '@/state/context/ConfigAndDataContext';
import Toast from '../../objects/Toast';
import ExportSettingsDialog from '@/components/avgrensning/shared/ExportSettingsDialog';
import {
    buildCsvLink,
    buildSosiLink,
    handleDownload,
    useIsSensitiveAvailable,
    useUrlsParams,
} from '@/utils/exportUtils';
import { useSelector } from 'react-redux';
import { stateOmrader, statePreferences } from '@/selectors/selectors';
import { getApiURLVegobjekter, getApiVersion } from '@/utils/Utils';

interface DownloadResultsBoxProps {
    votState: VegobjekttypeState;
    catState?: VegobjekttypeCategoryState;
}

export const DownloadResultsBox = (props: DownloadResultsBoxProps) => {
    const context = useContext(ConfigAndDataContext);

    const [exportDialogOpen, setExportDialogOpen] = useState(false);
    const [downloading, setDownloading] = useState(false);

    const isSensitiveAvailable = useIsSensitiveAvailable(props.votState.typeId);
    const urlsParams = useUrlsParams(props.votState, props.catState);

    const preferences = useSelector(statePreferences);
    const omraderState = useSelector(stateOmrader);

    const requireTypeId = (): number => {
        if (props.votState.typeId === null) {
            throw new Error('Kan ikke eksportere vegobjekttype uten type-ID');
        }
        return props.votState.typeId;
    };

    const handleQuickDownload = async (
        e: React.MouseEvent<HTMLAnchorElement, MouseEvent>,
        url: string
    ) => {
        await handleDownload(e, isSensitiveAvailable, url, context, setDownloading);
    };

    const convertFileSize = (fileSize: number) => {
        return Math.round(fileSize / 500); // returns approx. filesize in MB
    };

    const fileSizeWarning = () => {
        const { votState, catState } = props;
        if (
            votState.loading ||
            catState?.loading ||
            votState.result === null ||
            (catState && catState.result === null)
        ) {
            return null;
        }
        const result = catState?.result ?? votState.result;
        const extentStatistics = votState.result.extentStatistics;
        if (!catState && !extentStatistics) {
            return null;
        }
        const fileSize =
            catState && result
                ? convertFileSize(result.vegobjekter.length)
                : convertFileSize(extentStatistics?.antall ?? 0);
        return (
            fileSize > 5 && (
                <p className="c-results-summary-export-links__file-size-warning">
                    <strong>Advarsel: </strong>
                    Stor fil (ca. {fileSize} MB). Det kan ta opptil et par minutt før
                    filen begynner å laste ned.
                </p>
            )
        );
    };

    const apiLink = () => {
        const typeId = requireTypeId();
        const urlParams = `/vegobjekter/${typeId}?${urlsParams}`;
        return getApiURLVegobjekter(context.config) + urlParams;
    };

    const { restapi_url_v3, restapi_url, eksport_url, eksport_x_url } = context.config;
    const apiVersion = getApiVersion();
    const apiUrl = apiVersion === ApiVersion.V3 ? restapi_url_v3 : restapi_url;
    const eksportUrl =
        apiVersion === ApiVersion.V4 && eksport_x_url ? eksport_x_url : eksport_url;
    const eksportApiUrl =
        apiVersion === ApiVersion.V4 && eksport_x_url ? undefined : apiUrl;
    const typeId = requireTypeId();

    const csvLink = () =>
        buildCsvLink({
            typeId,
            baseUrlParams: urlsParams,
            eksportUrl: eksportUrl,
            apiUrl: eksportApiUrl,
        });

    const sosiLink = () =>
        buildSosiLink({
            typeId,
            baseUrlParams: urlsParams,
            eksportUrl: eksportUrl,
            apiUrl: eksportApiUrl,
            exportEnumFormat: preferences.exportEnumFormat,
            omraderState,
        });

    const csvTooltip = () => {
        return `Kartprojeksjon: ${preferences.exportProjection}`;
    };

    const sosiTooltip = () => {
        const enumFormat = preferences.exportEnumFormat || 'Ikke satt';
        return `Kartprojeksjon: ${preferences.exportProjection}\nTillatte verdier: ${enumFormat}`;
    };

    const apiTooltip = () => {
        return `Kartprojeksjon: ${preferences.exportProjection}`;
    };

    return (
        <div className="c-results-summary t-hidden-mobile">
            <div className="c-results-summary__export c-results-summary-export">
                <div className="c-results-summary-export__links c-results-summary-export-links t-clearfix">
                    <i
                        className="o-icon-download c-results-summary__icon-download"
                        aria-hidden={true}
                    />
                    <span className="c-results-summary__text">Last ned datasett:</span>
                    <a
                        key="csv-link"
                        className="c-results-summary-export-links__csv"
                        href={csvLink()}
                        onClick={e => handleQuickDownload(e, csvLink())}
                        title={csvTooltip()}
                    >
                        CSV{' '}
                        {isSensitiveAvailable && (
                            <i className="o-icon-logout c-results-summary__icon-lock" />
                        )}
                    </a>
                    <a
                        key="sosi-link"
                        className="c-results-summary-export-links__sosi"
                        href={sosiLink()}
                        onClick={e => handleQuickDownload(e, sosiLink())}
                        title={sosiTooltip()}
                    >
                        SOSI{' '}
                        {isSensitiveAvailable && (
                            <i className="o-icon-logout c-results-summary__icon-lock" />
                        )}
                    </a>
                    <a
                        key="api-link"
                        className="c-results-summary-export-links__api"
                        target="_blank"
                        rel="noopener noreferrer"
                        href={apiLink()}
                        title={apiTooltip()}
                    >
                        API
                    </a>
                    <button
                        title="Åpne innstillinger for eksport"
                        id="instillinger-link"
                        key="instillinger-link"
                        className="c-results-summary-export-dialog"
                        onClick={() => {
                            setExportDialogOpen(true);
                        }}
                    >
                        <i
                            className="material-icons"
                            aria-hidden="true"
                            style={{ color: exportDialogOpen ? 'orange' : 'inherit' }}
                        >
                            settings
                        </i>
                    </button>
                </div>
                {fileSizeWarning()}
            </div>
            <Toast text={'Nedlasting pågår'} show={downloading} />
            <ExportSettingsDialog
                isOpen={exportDialogOpen}
                setIsOpen={setExportDialogOpen}
            />
        </div>
    );
};
